<script setup>
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useAdmin } from "@/composables/useAdmin";
import { menuGroupsForArea, toInternalRoute } from "@/utils/aiCatalogData";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();

// 分组折叠状态管理：默认展开 "Image AI"
const expandedGroup = ref("Image AI");
// 为了做“收起时也有动画”，在收起瞬间先保留 DOM，等动画结束再卸载内容
const renderedExpandedGroup = ref(expandedGroup.value);
const expandedPanelHeight = ref(0);
const accordionAreaRef = ref(null);
/** 二级菜单展开区可视高度下限（px）；过小会导致几乎看不到条目 */
const MIN_SECONDARY_MENU_HEIGHT = 220;
let clearRenderedTimer = null;

// 在新标签页打开外链（用于 Free Tools 这类直接链接项）
const openExternal = (to) => {
  if (typeof window === "undefined") return;
  const href = /^https?:\/\//.test(to) ? to : `${window.location.origin}${to}`;
  window.open(href, "_blank", "noopener");
};

// 点击分组标题：若是外链项则新标签打开，否则按手风琴展开/折叠
const handleGroupHeaderClick = (group) => {
  if (group?.externalTo) {
    openExternal(group.externalTo);
    return;
  }
  toggleGroup(group.label);
};

// 切换分组展开状态（互斥：一次只能展开一个）
const toggleGroup = (groupLabel) => {
  if (expandedGroup.value === groupLabel) {
    // 如果点击的是已展开的分组，则折叠它
    expandedGroup.value = null;
  } else {
    // 否则展开点击的分组（会自动折叠之前展开的）
    expandedGroup.value = groupLabel;
  }
};

// 判断分组是否展开
const isGroupExpanded = (groupLabel) => {
  return expandedGroup.value === groupLabel;
};

const updateExpandedPanelHeight = async () => {
  await nextTick();
  const root = accordionAreaRef.value;
  if (!root) {
    expandedPanelHeight.value = 0;
    return;
  }
  // 固定部分：所有分组标题 + 分隔线（展开内容区高度 = 容器高度 - 固定部分高度）
  const fixedEls = root.querySelectorAll("[data-accordion-fixed='1']");
  let fixedHeight = 0;
  fixedEls.forEach((el) => {
    const box = el?.offsetHeight ?? 0;
    const style = el ? window.getComputedStyle(el) : null;
    const mt = style ? Number.parseFloat(style.marginTop || "0") : 0;
    const mb = style ? Number.parseFloat(style.marginBottom || "0") : 0;
    fixedHeight += box + mt + mb;
  });
  // 小窗时「全部一级标题」累加高度可能超过可视高度，remaining 会为负；
  // 仍为展开区保留最小高度，并由外层 overflow-y-auto 滚动整块手风琴，避免二级菜单不可见且无法点其他一级。
  const remaining = root.clientHeight - fixedHeight;
  expandedPanelHeight.value = Math.max(MIN_SECONDARY_MENU_HEIGHT, remaining);
};

watch(expandedGroup, (next) => {
  if (clearRenderedTimer) {
    clearTimeout(clearRenderedTimer);
    clearRenderedTimer = null;
  }
  if (next) {
    renderedExpandedGroup.value = next;
  } else {
    // 等折叠动画结束后再移除菜单内容
    clearRenderedTimer = setTimeout(() => {
      renderedExpandedGroup.value = null;
      clearRenderedTimer = null;
    }, 200);
  }
  updateExpandedPanelHeight();
});

const handleResize = () => updateExpandedPanelHeight();
onMounted(() => {
  window.addEventListener("resize", handleResize);
  updateExpandedPanelHeight();
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
  if (clearRenderedTimer) clearTimeout(clearRenderedTimer);
});

const accountBasePaths = [
  "/my/plan-credits",
  "/my/invoices",
  "/my/profile",
  "/my/earn-credits/",
];
const accountGroupLabel = "My Account";
const normalizeBasePath = (p) => (p.endsWith("/") ? p.slice(0, -1) : p);
const isAccountRoute = computed(() => {
  const path = route.path;
  return accountBasePaths.some((p) => path.startsWith(normalizeBasePath(p)));
});

// 登录态
const token = computed(() => store.state.user.token);

// 使用积分弹窗 composable
const { openBuyCreditsModal } = useCreditsModal();

const goEarnCredits = () => {
  router.push("/my/earn-credits/");
};

const menuData = inject("menuData", ref([]));

const navUi = {
  wrapper: "flex flex-col !gap-5",
  linkLabel:
    "text-[#E5E7EB] group-hover:text-[#8A53FC] group-data-[state=open]:text-[#8A53FC]",
  linkLeadingIcon:
    "text-[#E5E7EB] group-hover:text-[#8A53FC] group-data-[state=open]:text-[#8A53FC]",
  linkTrailingIcon:
    "text-[#E5E7EB] group-hover:text-[#8A53FC] group-data-[state=open]:text-[#8A53FC]",
  link: "!hover:text-[#8A53FC] !focus:text-[#8A53FC] !active:text-[#8A53FC]",
  childList: "bg-[#0F172B]",
  childLink:
    "text-[#E5E7EB] hover:text-[#8A53FC] bg-[#0F172B] hover:bg-[#0F172B] focus:bg-[#0F172B]",
  childLinkLabel: "text-[#E5E7EB] group-hover:text-[#8A53FC] size-5!",
  childLinkIcon: "text-[#E5E7EB] group-hover:text-[#8A53FC] size-5!",
};

const accountGroupItems = computed(() => [
  {
    label: "Refer a Friend",
    to: "/my/earn-credits/",
    active: route.path.startsWith("/my/earn-credits/"),
    icon: "i-lucide-party-popper",
  },
  {
    label: "My Plan & Credits",
    to: "/my/plan-credits",
    active: route.path.startsWith("/my/plan-credits"),
    icon: "material-symbols-light:diamond-shine-outline-rounded",
  },
  {
    label: "My Invoices",
    to: "/my/invoices",
    active: route.path.startsWith("/my/invoices"),
    icon: "material-symbols-light:receipt-rounded",
  },
  // {
  //   label: "My Profile",
  //   to: "/my/profile",
  //   active: route.path.startsWith("/my/profile"),
  //   icon: "i-lucide-user",
  // },
]);

const accountGroup = computed(() => ({
  label: accountGroupLabel,
  groupIcon: "i-lucide-user",
  items: accountGroupItems.value,
  collapsible: true,
}));

// Admin 菜单组
const adminGroupLabel = "Admin";
const adminGroupItems = computed(() => [
  {
    label: "Menu",
    to: "/admin/menu/",
    active: route.path.startsWith("/admin/menu"),
    icon: "mdi-menu", // 菜单图标
  },
  {
    label: "Banner",
    to: "/admin/banner/",
    active: route.path.startsWith("/admin/banner"),
    icon: "mdi-image", // 图片图标
  },
  {
    label: "AI Model Vendors",
    to: "/admin/ai-model-vendors/",
    active: route.path.startsWith("/admin/ai-model-vendors"),
    icon: "mdi-image-multiple", // 模型图标
  },
  {
    label: "AI Model Pages",
    to: "/admin/ai-model-pages/",
    active: route.path.startsWith("/admin/ai-model-pages"),
    icon: "mdi-brain", // 人工智能图标
  },
  {
    label: "AI Product Pages",
    to: "/admin/ai-product-pages/",
    active: route.path.startsWith("/admin/ai-product-pages"),
    icon: "mdi-cube", // 产品图标
  },
  {
    label: "AI Model Configs",
    to: "/admin/ai-model-configs/",
    active: route.path.startsWith("/admin/ai-model-configs"),
    icon: "mdi-cogs", // 设置图标
  },
  {
    label: "AI Effects Configs",
    to: "/admin/ai-effects-configs/",
    active: route.path.startsWith("/admin/ai-effects-configs"),
    icon: "mdi-auto-fix",
  },
  {
    label: "Workflow Admin",
    to: "/admin/test/workflow_admin/",
    active: route.path.startsWith("/admin/test/workflow_admin"),
    icon: "mdi-cogs",
  },
  {
    label: "Task Logs",
    to: "/admin/task-logs/",
    active: route.path.startsWith("/admin/task-logs"),
    icon: "i-lucide-circle-alert",
  },
]);
const adminGroup = computed(() => ({
  label: adminGroupLabel,
  groupIcon: "i-lucide-settings",
  items: adminGroupItems.value,
  collapsible: true,
}));

// Free Tools 组（单项直接链接：在新标签页打开 /free-tools/ 枢纽页，与主站视觉分开）
const freeToolsGroupLabel = "Free Tools";
const FREE_TOOLS_HUB_PATH = "/free-tools/";
const freeToolsGroup = computed(() => ({
  label: freeToolsGroupLabel,
  groupIcon: "material-symbols-light:qr-code-rounded",
  externalTo: FREE_TOOLS_HUB_PATH, // 标记为外链项：标题即链接，点击新标签打开，不可折叠
  collapsible: false,
}));

const startGroups = computed(() => ({
  label: null, // 没有标签，不可折叠
  items: [
    {
      label: "AI Effects",
      icon: "i-lucide-sparkles",
      to: "/ai/effects/",
      active: route.path.startsWith("/ai/effects"),
    },
    {
      label: "My Tasks",
      icon: "material-symbols-light:folder-check-rounded",
      to: "/my/tasks/",
      active: route.path.startsWith("/my/tasks"),
    },
    // {
    //   label: "All Tools",
    //   icon: "material-symbols-light:qr-code-rounded",
    //   to: "/app",
    //   target: "_blank",
    // },
  ],
  collapsible: false,
}));

const toolGroups = computed(() => {
  return menuGroupsForArea(menuData.value, "aside").map((group) => ({
    label: group.title,
    items: (group.children || []).map((item) => {
      const targetPath = toInternalRoute(item.url);
      const menuItem = {
        label: item.title,
        icon: item.icon,
        active: route.path.startsWith(targetPath.replace(/\/$/, "")),
        avatar: item.avatar,
      };
      if (item.target) {
        menuItem.target = item.target;
        menuItem.onSelect = (e) => {
          e.preventDefault();
          window.open(`${window.location.origin}${targetPath}`, "_blank");
        };
      } else {
        menuItem.to = targetPath;
      }
      if (item.disabled) menuItem.disabled = item.disabled;
      return menuItem;
    }),
    groupIcon: group.icon,
    collapsible: true,
  }));
});

const accordionGroups = computed(() => {
  const groups = [...toolGroups.value];
  groups.push(freeToolsGroup.value);
  if (token.value) groups.push(accountGroup.value);
  // 仅在管理员时添加 Admin 菜单组
  if (isAdmin.value) groups.push(adminGroup.value);
  return groups;
});

watch(accordionGroups, () => updateExpandedPanelHeight(), { immediate: true });

const activeAccordionGroupLabel = computed(() => {
  // 检查是否是 Admin 路由
  if (isAdmin.value && route.path.startsWith("/admin/")) {
    return adminGroupLabel;
  }
  // Free Tools 是新标签打开的外链枢纽页，主站不再以它作为激活分组
  if (token.value && isAccountRoute.value) return accountGroupLabel;
  for (const group of menuGroupsForArea(menuData.value, "aside")) {
    for (const item of group.children || []) {
      const targetPath = toInternalRoute(item.url).replace(/\/$/, "");
      if (route.path.startsWith(targetPath)) return group.title;
    }
  }
  return null;
});

watch(
  () => route.path,
  () => {
    // 路由命中某个工具时，自动展开其所在分组（便于在"仅组内滚动"的布局中看到当前项）
    if (activeAccordionGroupLabel.value) {
      expandedGroup.value = activeAccordionGroupLabel.value;
    } else {
      // 如果当前菜单没有激活项，全部折叠起来
      expandedGroup.value = null;
    }
  },
  { immediate: true }
);

watch(
  accordionGroups,
  (next) => {
    // 仅在当前 expandedGroup 不存在时兜底到第一个分组；保留用户手动“全部折叠”(null)
    if (expandedGroup.value === null) return;
    if (!next.some((g) => g.label === expandedGroup.value)) {
      expandedGroup.value = next[0]?.label ?? null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <UDashboardSidebar
    :ui="{
      // 只让“展开分组的内容区”滚动，sidebar body 本身不滚动
      body: '!overflow-y-hidden !overflow-x-hidden !px-2 !py-2 !gap-1 select-none',
      footer: '!overflow-y-hidden !overflow-x-hidden !px-2 !py-2 !gap-1',
    }"
    class="bg-[#0F172B] min-h-0 border-none"
  >
    <template #default="{ collapsed }">
      <!-- 仅组内滚动：startGroups + 组标题固定；内容区独立滚动 -->
      <div class="flex flex-col w-full h-full min-h-0 overflow-hidden">
        <div class="shrink-0">
          <UNavigationMenu
            :collapsed="collapsed"
            :items="startGroups.items"
            orientation="vertical"
            :ui="{
              ...navUi,
              linkLabel:
                'text-[#E5E7EB] group-hover:text-[#8A53FC] group-data-[state=open]:text-[#8A53FC] font-bold',
              linkLeadingIcon:
                'text-[#E5E7EB] group-hover:text-[#8A53FC] group-data-[state=open]:text-[#8A53FC] size-5',
            }"
          />
          <USeparator v-if="accordionGroups.length" class="my-1" />
        </div>

        <!-- 手风琴：标题在各自子元素上方；仅展开分组内容区滚动，标题不随滚动隐藏 -->
        <div
          v-if="collapsed"
          class="flex-1 min-h-0 overflow-y-auto right-scroll"
        >
          <template
            v-for="(group, index) in accordionGroups"
            :key="group.label"
          >
            <!-- 外链项（Free Tools）：渲染为新标签打开的单个链接；否则渲染分组 items -->
            <UNavigationMenu
              :collapsed="collapsed"
              :items="
                group.externalTo
                  ? [
                      {
                        label: group.label,
                        icon: group.groupIcon,
                        to: group.externalTo,
                        target: '_blank',
                        rel: 'noopener',
                        onSelect: (e) => {
                          e.preventDefault();
                          openExternal(group.externalTo);
                        },
                      },
                    ]
                  : group.items
              "
              orientation="vertical"
              :ui="navUi"
            />
            <USeparator
              v-if="index < accordionGroups.length - 1"
              class="my-1"
            />
          </template>
        </div>
        <div
          v-else
          ref="accordionAreaRef"
          class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden right-scroll flex flex-col"
        >
          <template
            v-for="(group, index) in accordionGroups"
            :key="group.label"
          >
            <div
              class="group shrink-0 px-2.5 py-1.5 cursor-pointer hover:bg-white/5 rounded-md transition-colors flex items-center justify-between gap-2 mb-1"
              data-accordion-fixed="1"
              :role="group.externalTo ? 'link' : 'button'"
              @click="handleGroupHeaderClick(group)"
            >
              <div class="flex items-center gap-2 flex-1">
                <UIcon
                  v-if="group.groupIcon"
                  :name="group.groupIcon"
                  class="group-hover:text-[#8A53FC] size-6"
                  :class="
                    isGroupExpanded(group.label)
                      ? 'text-[#8A53FC]'
                      : 'text-[#E5E7EB]'
                  "
                />
                <span
                  class="text-sm font-bold group-hover:text-[#8A53FC]"
                  :class="
                    isGroupExpanded(group.label)
                      ? 'text-[#8A53FC]'
                      : 'text-[#E5E7EB]'
                  "
                >
                  {{ group.label }}
                </span>
              </div>
              <!-- 外链项显示“新标签打开”图标；普通分组显示展开/折叠箭头 -->
              <UIcon
                v-if="group.externalTo"
                name="i-lucide-external-link"
                class="size-4 text-[#E5E7EB] group-hover:text-[#8A53FC] transition-colors"
              />
              <UIcon
                v-else
                :name="
                  isGroupExpanded(group.label)
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
                class="size-4 group-hover:text-[#8A53FC] transition-transform duration-200"
                :class="[
                  isGroupExpanded(group.label)
                    ? 'text-[#8A53FC]'
                    : 'text-[#E5E7EB]',
                  isGroupExpanded(group.label) ? 'rotate-0' : '-rotate-90',
                ]"
              />
            </div>

            <!-- 展开/折叠动画：高度从 0 -> 可用高度（让其他标题跟随“推开”，类似 My Account） 缩进取消 -->
            <!-- 外链项（Free Tools）没有展开内容，跳过面板 -->
            <div
              v-if="!group.externalTo"
              class="overflow-hidden transition-[height,opacity] duration-200 ease-in-out"
              :style="{
                height: isGroupExpanded(group.label)
                  ? `${expandedPanelHeight}px`
                  : '0px',
                minHeight: isGroupExpanded(group.label)
                  ? `${MIN_SECONDARY_MENU_HEIGHT}px`
                  : '0px',
              }"
              :class="
                isGroupExpanded(group.label)
                  ? 'opacity-100'
                  : 'opacity-0 pointer-events-none'
              "
            >
              <div class="h-full min-h-0 overflow-y-auto right-scroll">
                <template v-if="renderedExpandedGroup === group.label">
                  <UNavigationMenu
                    :collapsed="collapsed"
                    :items="group.items"
                    orientation="vertical"
                    :ui="navUi"
                  />
                </template>
              </div>
            </div>

            <!-- <div
              v-if="index < accordionGroups.length - 1"
              class="shrink-0 my-1"
              data-accordion-fixed="1"
            >
              <USeparator />
            </div> -->
          </template>
        </div>
      </div>
    </template>

    <template #footer="{ collapsed }">
      <div class="space-y-3 w-full flex flex-col items-center" v-if="token">
        <!-- Refer a Friend（跳转 /my/earn-credits/） -->
        <button
          v-if="!collapsed"
          type="button"
          class="w-full text-left rounded-lg px-3 py-2 bg-white/5 hover:bg-white/10 transition-colors"
          @click="goEarnCredits"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2 min-w-0">
              <UIcon
                name="i-lucide-party-popper"
                class="size-5 text-[#8A53FC] shrink-0"
              />
              <div class="min-w-0">
                <div class="text-sm font-semibold text-amber-300 truncate">
                  Refer a Friend
                </div>
                <div class="text-xs text-white/60 truncate">
                  Get 10 credits each
                </div>
              </div>
            </div>
            <UIcon name="i-lucide-chevron-right" class="size-4 text-white/60" />
          </div>
        </button>
      </div>
    </template>
  </UDashboardSidebar>
</template>
